import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import './TMap.css';
import css from '../../traffic/TReportCenter/Report.module.css';
import {Fragment, useEffect, useMemo, useRef, useState} from "react";
import { HeatmapLayer } from "react-leaflet-heatmap-layer-v3";
import MapServiceTZ from '../../../services/MapServiceTZ';
import Settings from '../../../components/Settings/Settings';
import SFilterSettings from '../../../components/Search/SFilterSettings';
import ShowBox from '../../../components/ShowBox';
import SFilterValueDraw from '../../../components/Search/SFilterValueDraw';
import SearchServiceTZ from '../../../services/SearchServiceTZ';
import { SearchFilter, SearchFilterValue } from '../../../interfaces/SearchFilter';
import filterItemsTrafficAnimals from '../../../interfaces/ConstFilterTrafficAnimals';
import { MdLocalPolice } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { FaMap } from 'react-icons/fa';
import { Grow, GrowProps, Snackbar, Stack } from '@mui/material';
import trafficSk from '../../../language/traffic/_sk.json';
// import CircularProgress from '@mui/material/CircularProgress';

// Automaticky vytvoriť reverse mapu zo slovenského jazykového súboru
const createValueToKeyMap = (): Record<string, string> => {
    const map: Record<string, string> = {};
    
    // TGraphs.hodnotyGrafov
    if (trafficSk.TGraphs?.hodnotyGrafov) {
        Object.entries(trafficSk.TGraphs.hodnotyGrafov).forEach(([key, value]) => {
            map[value as string] = `TGraphs.hodnotyGrafov.${key}`;
        });
    }
    
    // Map.pricinaNehody
    if (trafficSk.Map?.pricinaNehody) {
        Object.entries(trafficSk.Map.pricinaNehody).forEach(([key, value]) => {
            map[value as string] = `Map.pricinaNehody.${key}`;
        });
    }
    
    return map;
};

const valueToKeyMap = createValueToKeyMap();
const tzMapDataCache = new Map<string, TZMapData[]>();

// Pre-vytvorené ikony podľa závažnosti
const markerIcons: Record<string, L.Icon> = {
  red:    new L.Icon({ iconSize: [25, 41], iconAnchor: [10, 41], popupAnchor: [2, -40], iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',    shadowUrl: 'https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png' }),
  orange: new L.Icon({ iconSize: [25, 41], iconAnchor: [10, 41], popupAnchor: [2, -40], iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png', shadowUrl: 'https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png' }),
  yellow: new L.Icon({ iconSize: [25, 41], iconAnchor: [10, 41], popupAnchor: [2, -40], iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png', shadowUrl: 'https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png' }),
  green:  new L.Icon({ iconSize: [25, 41], iconAnchor: [10, 41], popupAnchor: [2, -40], iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',  shadowUrl: 'https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png' }),
  blue:   new L.Icon({ iconSize: [25, 41], iconAnchor: [10, 41], popupAnchor: [2, -40], iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',   shadowUrl: 'https://unpkg.com/leaflet@1.6/dist/images/marker-shadow.png' }),
};

interface TZMapData {
  id: string;
  x: string;
  y: string;
  celkovaHmotnaSkoda: string;
  usmrteneOsoby: string;
  tazkoZraneneOsoby: string;
  lahkoZraneneOsoby: string;
  pricinaNehody: string;
  druhZveri: string;
  datum: string;
  cas: string;
  zavaznost: string;
  kraj: string;
  viditelnost: string;
  alkohol: string;
  povrchVozovky: string;
  znackaVozidla: string;
}

export default function TZMap() {
  const { t, i18n } = useTranslation(["traffic", "crimes"]);
  const [ mapDataTZ, setMapDataTZ ] = useState<TZMapData[] | null>(null);
  const [ filter, setFilter ] = useState<SearchFilter[]>(filterItemsTrafficAnimals);
  const [ filterValues, setFilterValues ] = useState<SearchFilterValue[]>([{
    type: {
        value: "AND",
        label: t("analyzis.AND")
    },
    value: [
        {
            type: {
                value: "AND",
                label: t("analyzis.AND")
            },
            atribute: {
                value: "datum",
                label: t("crimes:Filters.datum")
            },
            option: {
                value: "DATE_BETWEEN",
                label: t("analyzis.DATE_BETWEEN")
            },
            value: [
                "2025-01-01T00:00:00.000Z",
                "2025-12-30T23:59:59.000Z"
            ]
        }
    ]
  }]);

  const [ loading , setLoading ] = useState(true);
  const [ showHeatmap, setShowHeatmap ] = useState(true);
  const [ showSnackbar, setShowSnackbar ] = useState(false);
  const [ countValues, setCountValues ] = useState(0);
  const [progress, setProgress] = useState(0);
  const requestIdRef = useRef(0);

  const payload = useMemo(() => JSON.stringify(
    filterValues?.map((value) => ({
      type: value.type.value,
      value: value.value.map((val) => ({
          type: val.type.value,
          atr: val.atribute.value,
          option: val.option.value,
          value: val.value.map((valInVal) => valInVal.value ? valInVal.value : isNaN(valInVal) ? new Date(valInVal).toISOString() : valInVal)
      }))
    }))
  ), [filterValues]);

  useEffect(() => {
    setFilterValues(prev => prev.map(v => ({
      ...v,
      type: { ...v.type, label: t(`analyzis.${v.type.value}`) },
      value: v.value.map(inner => ({
        ...inner,
        type: { ...inner.type, label: t(`analyzis.${inner.type.value}`) },
        atribute: { ...inner.atribute, label: t(`crimes:Filters.${inner.atribute.value}`, inner.atribute.label) },
        option: { ...inner.option, label: t(`analyzis.${inner.option.value}`) },
      }))
    })));
  }, [i18n.language]);

  const handleFilterValuesChange = (newValues: SearchFilterValue[]) => {
    setFilterValues(newValues);
  }

  const handleFilterChange = (newFilter: SearchFilter[], newValues: SearchFilterValue[]) => {
    setFilter(newFilter);
    setFilterValues(newValues);
  }

  const handleImportData = (data: any) => {
      setFilter(data?.filter);
      setFilterValues(data?.filterValues);
  }

  useEffect(() => {
    const cached = tzMapDataCache.get(payload);
    if (cached) {
      setMapDataTZ(cached);
      setCountValues(cached.length);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    const timer = setTimeout(() => {
      MapServiceTZ.getMapData(payload)
        .then((resp: any) => {
          if (requestId !== requestIdRef.current) return;
          const data = resp.data.data as TZMapData[];
          tzMapDataCache.set(payload, data);
          setMapDataTZ(data);
          setCountValues(data.length);
          setLoading(false);
          setShowSnackbar(true);
        })
        .catch(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    }, 220);

    return () => clearTimeout(timer);
  }, [payload]);

  const heatmapOptions = {
    radius: 16,
    blur: 35,
    maxZoom: 18,
    minOpacity: 0.2,
    maxOpacity: 1
  };

  const dataToFile = {
    filter: filter,
    filterValues: filterValues
  };

  const toggleMap = () => {
    setShowHeatmap(!showHeatmap);
  };

  const MARKER_LIMIT = 20000;

  const validMarkerData = useMemo(
    () => mapDataTZ?.filter((data) => !isNaN(parseFloat(data.y)) && !isNaN(parseFloat(data.x))) ?? [],
    [mapDataTZ]
  );

  const heatmapData = useMemo(
    () => validMarkerData.map((data: TZMapData) => [parseFloat(data.y), parseFloat(data.x)] as [number, number]),
    [validMarkerData]
  );

  const markerDataLimited = useMemo(
    () => validMarkerData.slice(0, MARKER_LIMIT),
    [validMarkerData]
  );

  const isLimited = !showHeatmap && validMarkerData.length > MARKER_LIMIT;

  const getMarkerColor = (zavaznost: string) => {
    switch (zavaznost) {
      case '3':
        return 'red';
      case '2':
        return 'orange';
      case '1':
        return 'yellow';
      case '0':
        return 'green';
      default:
        return 'blue'; 
    }
  };


  function MultipleMarkers() {
    if (!markerDataLimited.length) return null;
    return (
      <Fragment>
        {markerDataLimited.map((data, index) => (
          <Marker key={index} position={[parseFloat(data.y), parseFloat(data.x)]} icon={markerIcons[getMarkerColor(data.zavaznost)] ?? markerIcons.blue}>
            <Popup>
              <table>
                <tbody>
                  <tr>
                    <td><strong>{t("Map.kraj")}</strong></td>
                    <td>{data.kraj || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.nehodaDatum")}</strong></td>
                    <td>{data.datum || 'N/A'}   {data.cas || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.celkovaSkoda")}</strong></td>
                    <td>{data.celkovaHmotnaSkoda || 'N/A'} Kč</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.osobyUsmrt")}</strong></td>
                    <td style={{ color: 'red' }}>{data.usmrteneOsoby || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.osobyTazZran")}</strong></td>
                    <td style={{ color: 'orange' }}>{data.tazkoZraneneOsoby || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.osobyLahZran")}</strong></td>
                    <td style={{ color: '#FFD700' }}>{data.lahkoZraneneOsoby || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td style={{ minWidth: '140px' }}><strong>{t("Map.druhZveri")}</strong></td>
                    <td>{data.druhZveri ? (valueToKeyMap[data.druhZveri] ? t(`traffic:${valueToKeyMap[data.druhZveri]}`) : data.druhZveri) : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.znackaVoz")}</strong></td>
                    <td>{data.znackaVozidla || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.povrchVozovky")}</strong></td>
                    <td>{data.povrchVozovky ? (valueToKeyMap[data.povrchVozovky] ? t(`traffic:${valueToKeyMap[data.povrchVozovky]}`) : data.povrchVozovky) : 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.alkohol")}</strong></td>
                    <td>{(() => {
                      if (!data.alkohol) return 'N/A';
                      const trimmedValue = data.alkohol.trim();
                      const translationKey = valueToKeyMap[trimmedValue];
                      return translationKey ? t(`traffic:${translationKey}`) : trimmedValue;
                    })()}</td>
                  </tr>
                  <tr>
                    <td><strong>{t("Map.viditelnost")}</strong></td>
                    <td>{(() => {
                      if (!data.viditelnost) return 'N/A';
                      const trimmedValue = data.viditelnost.trim();
                      const translationKey = valueToKeyMap[trimmedValue];
                      return translationKey ? t(`traffic:${translationKey}`) : trimmedValue;
                    })()}</td>
                  </tr>
                </tbody>
              </table>
            </Popup>
          </Marker>
        ))}
      </Fragment>
    );
  }

  function GrowTransition(props: GrowProps) {
    return <Grow {...props} />;
  }
  
  const handleCloseSnackbar = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway')
      return;

    setShowSnackbar(false);
  };

  function updateProgressBar(processed: any, total: any, elapsed: any) {
    if (elapsed > 1000) {
      // if it takes more than a second to load, display the progress bar:
      // progress.style.display = 'block';
      // progressBar.style.width = Math.round(processed/total*100) + '%';
      setProgress(Math.round(Number(processed/total*100)))
      setLoading(true);
    }

    if (processed === total) {
      // all markers processed - hide the progress bar:
      // progress.style.display = 'none';
      setProgress(0);
      setLoading(false);
    }
  }

  return (
    <>
    
      <div className="box-12 py-2 text-right form">

       

        <SFilterSettings 
            filter={filter} 
            filterValues={filterValues}
            onFilterChange={handleFilterChange} 
            endpointService={SearchServiceTZ}
        />
      </div>
      <ShowBox className="box-12 py-2" name="">
        <SFilterValueDraw 
          filterValues={filterValues} 
          onChange={handleFilterValuesChange} 
        />
      </ShowBox>
        
      {/* <Stack spacing={2} direction="row">
      <CircularProgress variant="determinate" value={progress} /></Stack> */}

      <div style={{ marginTop: '50px', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '26px' }}>
          <strong>{showHeatmap ? t("Map.heatNadpis") : t("Map.baseNadpis")}</strong>
        </h1>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <FaMap name='pinIcon' size={24} style={{ marginRight: '10px', color: 'black', cursor: 'pointer' }} />
          <div className={`toggle-switch ${showHeatmap ? 'checked' : ''}`} onClick={toggleMap}>
            <div className="slider"/>
          </div>
        </div>
        {isLimited && (
          <div style={{ display: 'inline-block', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '8px 14px', fontSize: '13px', color: '#856404' }}
            dangerouslySetInnerHTML={{ __html: t('Map.limitInfo', { shown: `<strong>${MARKER_LIMIT.toLocaleString()}</strong>`, total: `<strong>${validMarkerData.length.toLocaleString()}</strong>` }) as string }}
          />
        )}
      </div>
      {/* <Stack spacing={2} direction="row">
      <CircularProgress variant="determinate" value={progress} /></Stack> */}
      <div id="map">
      <ShowBox className="box-12 py-2" name="">
        <MapContainer center={[49.8, 15.5]} zoom={7.5} style={{ zIndex: 1, width: '100%'}} maxZoom={18}>
          { loading && <div className={css.loading}>
            <div><MdLocalPolice /> {t("Traffic.loading")}</div>
          </div> }
          {showHeatmap && mapDataTZ !== null && mapDataTZ.length > 0 &&
              <HeatmapLayer
                fitBoundsOnLoad
                fitBoundsOnUpdate
                points={heatmapData}
                latitudeExtractor={(point: any) => point[0]}
                longitudeExtractor={(point: any) => point[1]}
                key={Math.random() + Math.random()}
                intensityExtractor={(point: any) => parseFloat(point[0])}
                {...heatmapOptions}
              />
          }

          {!showHeatmap && mapDataTZ !== null && mapDataTZ.length > 0 &&
              <MarkerClusterGroup /*chunkProgress={updateProgressBar}*/ chunkedLoading maxClusterRadius={100} removeOutsideVisibleBounds disableClusteringAtZoom={17} spiderfyDistanceMultiplier={2}>
                <MultipleMarkers />
              </MarkerClusterGroup>
          }
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </MapContainer>
      </ShowBox>
      </div>
      {/* <Stack spacing={2} direction="row">
      <CircularProgress variant="determinate" value={progress} /></Stack> */}

      <Snackbar
        open={showSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={GrowTransition}
        message={t("Map.pocetNehod", { count: countValues })}
        onClose={handleCloseSnackbar}
        autoHideDuration={5000}
        color="secondary"
        ContentProps={{
          sx: {
            display: 'block',
            textAlign: "center",
            color: 'white',
            backgroundColor: '#1F98F5',
          }
        }}
      />
    </>
  );
}